import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { AlertTriangle, Trash2, Sparkles, Send, X, Calendar, MapPin } from 'lucide-react';

export interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'publish' | 'warning';
  previewImage?: string;
  previewDetails?: {
    date?: string;
    place?: string;
    message?: string;
    mediaCount?: number;
    author?: string;
  };
  isLoading?: boolean;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmText,
  cancelText = 'Cancelar',
  type = 'danger',
  previewImage,
  previewDetails,
  isLoading = false,
}) => {
  if (!isOpen) return null;

  const isDanger = type === 'danger';
  const isPublish = type === 'publish';

  const defaultConfirmText = isDanger
    ? 'Sí, eliminar'
    : isPublish
    ? 'Sí, publicar'
    : 'Confirmar';

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/35 backdrop-blur-md animate-fadeIn">
        {/* Backdrop click */}
        <div className="absolute inset-0" onClick={isLoading ? undefined : onClose} />

        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          transition={{ duration: 0.2 }}
          className="relative bg-white text-[#302B30] rounded-3xl shadow-2xl border border-[#F0DDE4] max-w-md w-full overflow-hidden z-10"
        >
          {/* Top colored accent banner */}
          <div
            className={`h-2.5 w-full ${
              isDanger
                ? 'bg-gradient-to-r from-rose-500 via-red-500 to-rose-600'
                : 'bg-gradient-to-r from-[#E85D8E] via-[#E83E78] to-[#FF8DAF]'
            }`}
          />

          <div className="p-6">
            {/* Header with Icon */}
            <div className="flex items-start gap-3.5 mb-4">
              <div
                className={`p-3 rounded-2xl shrink-0 ${
                  isDanger
                    ? 'bg-rose-50 text-rose-600 border border-rose-200'
                    : 'bg-[#FFF4F7] text-[#E83E78] border border-[#F0DDE4]'
                }`}
              >
                {isDanger ? (
                  <Trash2 className="w-6 h-6" />
                ) : isPublish ? (
                  <Sparkles className="w-6 h-6" />
                ) : (
                  <AlertTriangle className="w-6 h-6" />
                )}
              </div>

              <div className="flex-1 min-w-0 pr-6">
                <h3 className="font-['Playfair_Display',serif] text-lg sm:text-xl font-bold text-[#302B30] leading-snug">
                  {title}
                </h3>
                <p className="text-xs sm:text-sm text-[#77717A] mt-1 leading-relaxed">
                  {description}
                </p>
              </div>

              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="absolute top-5 right-5 p-1.5 text-[#77717A] hover:text-[#302B30] rounded-full hover:bg-[#FFF4F7] transition-colors disabled:opacity-50 cursor-pointer"
                title="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Optional Visual Preview Card */}
            {(previewImage || previewDetails) && (
              <div className="my-4 p-3 bg-[#FFF4F7] border border-[#F0DDE4] rounded-2xl flex gap-3 items-center">
                {previewImage && (
                  <div className="relative w-16 h-16 rounded-xl overflow-hidden bg-white shrink-0 border border-[#F0DDE4] shadow-2xs">
                    <img
                      src={previewImage}
                      alt="Vista previa"
                      referrerPolicy="no-referrer"
                      className="w-full h-full object-cover"
                    />
                    {previewDetails?.mediaCount && previewDetails.mediaCount > 1 && (
                      <span className="absolute bottom-1 right-1 bg-black/75 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-md">
                        +{previewDetails.mediaCount}
                      </span>
                    )}
                  </div>
                )}

                <div className="flex-1 min-w-0 text-xs">
                  {previewDetails?.place && (
                    <div className="flex items-center gap-1.5 text-[#302B30] font-semibold truncate">
                      <MapPin className="w-3.5 h-3.5 text-[#E83E78] shrink-0" />
                      <span className="truncate">{previewDetails.place}</span>
                    </div>
                  )}
                  {previewDetails?.date && (
                    <div className="flex items-center gap-1.5 text-[#77717A] text-[11px] mt-0.5">
                      <Calendar className="w-3 h-3 text-[#77717A] shrink-0" />
                      <span>{previewDetails.date}</span>
                    </div>
                  )}
                  {previewDetails?.author && (
                    <div className="flex items-center gap-1.5 text-[#302B30] text-[11px] font-medium mt-1">
                      <span className="text-xs">✍️</span>
                      <span className="truncate">{previewDetails.author}</span>
                    </div>
                  )}
                  {previewDetails?.message && (
                    <p className="text-[#302B30] text-[11px] mt-1 line-clamp-2 italic font-serif bg-white px-2 py-1 rounded-lg border border-[#F0DDE4]">
                      "{previewDetails.message}"
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-2.5 mt-6 pt-3 border-t border-[#F0DDE4]">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="px-4 py-2.5 text-xs sm:text-sm font-bold text-[#302B30] bg-white hover:bg-[#FFF4F7] border border-[#F0DDE4] rounded-xl transition-all cursor-pointer disabled:opacity-50 shadow-2xs"
              >
                {cancelText}
              </button>

              <button
                type="button"
                onClick={onConfirm}
                disabled={isLoading}
                className={`px-5 py-2.5 text-xs sm:text-sm font-bold text-white rounded-xl shadow-md transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50 ${
                  isDanger
                    ? 'bg-rose-600 hover:bg-rose-700 shadow-rose-600/20 active:scale-98'
                    : 'bg-gradient-to-r from-[#E85D8E] to-[#E83E78] hover:opacity-95 shadow-pink-500/25 active:scale-98'
                }`}
              >
                {isLoading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    <span>Procesando...</span>
                  </>
                ) : (
                  <>
                    {isDanger ? <Trash2 className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                    <span>{confirmText || defaultConfirmText}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
